import { Component, Input, OnChanges, OnDestroy, OnInit } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subject, switchMap, takeUntil, timer } from 'rxjs';
import { IdentityReportsService } from '@app/core/services/identity-reports.service';
import { Client } from '@app/core/types/client';
import { BrandCodes } from '@app/core/types/brand';
import {
  ID_VERIFICATION_STATUS,
  SLOT_CONFIGS,
  SLOT_KEYS,
} from '@app/core/types/id-verification';
import moment from 'moment';
import { IdVerificationSocket } from '@app/main/socket-io/id-verification-socket.service';


@Component({
  selector: 'itfg-identity-verification',
  templateUrl: './identity-verification.component.html',
  styleUrl: './identity-verification.component.scss',
  standalone: false,
})
export class IdentityVerificationComponent implements OnInit, OnChanges, OnDestroy {
  @Input() client: Client;

  verifications: any[] = [];
  currentIndex = 0;
  currentDetail: any | null = null;
  loadingList = false;
  loadingDetail = false;
  expanded = false;
  selectedEventId: string | number | null = null;

  private _unsubscribe = new Subject<void>();

  // ── Display config ────────────────────────────────────────────────

  readonly STATUS_ICON: Record<ID_VERIFICATION_STATUS, string> = {
    [ID_VERIFICATION_STATUS.IN_PROGRESS]: 'hourglass_empty',
    [ID_VERIFICATION_STATUS.COMPLETED]: 'check_circle',
    [ID_VERIFICATION_STATUS.MANUAL]: 'person',
    [ID_VERIFICATION_STATUS.FAILED]: 'cancel',
  };

  readonly STATUS_CLASS: Record<ID_VERIFICATION_STATUS, string> = {
    [ID_VERIFICATION_STATUS.IN_PROGRESS]: 'pending',
    [ID_VERIFICATION_STATUS.COMPLETED]: 'approved',
    [ID_VERIFICATION_STATUS.MANUAL]: 'expired',
    [ID_VERIFICATION_STATUS.FAILED]: 'rejected',
  };

  readonly PROC_COLOR: Record<string, string> = {
    SHUFTI: '#6a1b9a',
    OPERATOR: '#e65100',
    INTERNAL: '#546e7a',
    EXTERNAL: '#4527a0',
    SYSTEM: '#90a4ae',
  };

  ngOnInit() {
    this.idVerificationSocket
      .onVerificationUpdated()
      .pipe(takeUntil(this._unsubscribe))
      .subscribe((verification: any) => { // TODO: create IdVerificationDto
        console.log("GAAAZ soketo", verification)
        this.service.identityVerificationDetail$.next(verification);
        this.currentDetail = verification;
      });
  }

  // ── Stats ─────────────────────────────────────────────────────────

  get total(): number {
    return this.verifications.length;
  }

  get completed(): number {
    return this.verifications.filter(v => v.status === ID_VERIFICATION_STATUS.COMPLETED).length;
  }

  get manual(): number {
    return this.verifications.filter(v => v.status === ID_VERIFICATION_STATUS.MANUAL).length;
  }

  get inProgress(): number {
    return this.verifications.filter(v => v.status === ID_VERIFICATION_STATUS.IN_PROGRESS).length;
  }

  get last24h(): number {
    const cutoff = Date.now() - 86_400_000;
    return this.verifications.filter(v => new Date(v.startedAt).getTime() > cutoff).length;
  }

  get events(): any[] {
    return [...(this.currentDetail?.events ?? [])];
  }

  get currentIsActive() {
    return this.currentDetail?.status === 'IN_PROGRESS' || this.currentDetail?.status === 'MANUAL';
  }

  constructor(
    private service: IdentityReportsService,
    private snackBar: MatSnackBar,
    private idVerificationSocket: IdVerificationSocket,
  ) { }

  // ── Lifecycle ─────────────────────────────────────────────────────

  ngOnChanges(): void {
    if (this.client?.id && this.client?.civilId) {
      this.loadList();
    }
  }

  // ── Data ──────────────────────────────────────────────────────────

  loadList(): void {
    this.loadingList = true;
    this.verifications = [];
    this.currentIndex = 0;
    // Hard-clear only on full client reset
    this.currentDetail = null;
    this.service.identityVerificationDetail$.next(null);

    this.service
      .getIdentityVerificationList(this.client.civilId, this.client.brand.code as BrandCodes)
      .pipe(takeUntil(this._unsubscribe))
      .subscribe({
        next: (data: any[]) => {
          this.verifications = data;
          if (data.length) {
            this.loadDetail(data[0].id);
          }
        },
        error: e => console.error(e),
        complete: () => {
          this.loadingList = false;
        },
      });
  }

  loadDetail(id: number): void {
    this.loadingDetail = true;
    // Keep stale currentDetail and detail$ as-is while loading —
    // the row dims via iv-row--loading, no blanking flash.

    this.service
      .getIdentityVerificationReport(id)
      .pipe(takeUntil(this._unsubscribe))
      .subscribe({
        next: (data: any) => {
          if (this.currentDetail) {
            this.idVerificationSocket.leaveVerificationRoom(this.currentDetail.id);
          }
          this.currentDetail = data;
          this.idVerificationSocket.joinVerificationRoom(this.currentDetail.id);

          this.service.identityVerificationDetail$.next(data);
        },
        error: e => console.error(e),
        complete: () => {
          this.loadingDetail = false;
        },
      });
  }

  toggleExpanded(): void {
    this.expanded = !this.expanded;
  }

  toggleEvent(id: string | number): void {
    this.selectedEventId = this.selectedEventId === id ? null : id;
  }

  /** Flattens ev.data into [key, value] pairs for display. */
  eventDataEntries(ev: any): [string, unknown][] {
    if (!ev?.data) {
      return [];
    }

    let data = ev.data;

    if (typeof data === 'string') {
      try {
        data = JSON.parse(data);
      } catch {
        return [];
      }
    }

    if (typeof data !== 'object') {
      return [];
    }

    return Object.entries(data).map(([k, v]) => {
      let display: unknown;

      if (v === null || v === undefined) {
        display = '—';
      } else if (typeof v === 'object') {
        display = JSON.stringify(v, null, 2);
      } else {
        display = v; // string/number/boolean render as-is, no quotes
      }

      return [k, display] as [string, unknown];
    });
  }
  // ── Actions ───────────────────────────────────────────────────────

  startVerification(): void {
    this.service
      .startIdentityVerification(this.client.civilId, this.client.brand.code as BrandCodes)
      .pipe(takeUntil(this._unsubscribe))
      .subscribe({
        next: () => this.loadList(),
        error: e => console.error(e),
      });

    this.snackBar.open('Verification started', 'OK', { duration: 2000 });
  }

  decide(action: 'complete' | 'reject'): void { // TODO: add type to action
    this.service.changeVerificationStatus$(this.verifications[this.currentIndex].id, action)
      .pipe(takeUntil(this._unsubscribe))
      .subscribe({
        next: () => {
          const label = action === 'complete' ? 'Approved' : 'Rejected';
          this.snackBar.open(`${label} #${this.currentDetail?.id}`, 'OK', { duration: 2000 }); // TODO: use notification service
        },
        error: e => {
          const label = action === 'complete' ? 'Approved' : 'Rejected';
          this.snackBar.open(`${label} #${this.currentDetail?.id}`, 'error', { panelClass: 'notification-snackbar-error', duration: 2000 });
          console.error(e)
        },
      });
  }

  // ── Helpers ───────────────────────────────────────────────────────

  statusClass(status: string): string {
    return this.STATUS_CLASS[status as ID_VERIFICATION_STATUS] ?? 'expired';
  }

  procColor(processor: string): string {
    return this.PROC_COLOR[processor] ?? '#546e7a';
  }

  isFailEvent(type: string): boolean {
    return /FAIL|REJECT|ERROR/.test(type);
  }

  isSuccessEvent(type: string): boolean {
    return /APPROVE|COMPLETE|MATCH/.test(type);
  }

  duration(startedAt: string, completedAt: string | null): string {
    if (!completedAt) {
      return 'ongoing';
    }

    const totalSeconds = Math.round(moment(completedAt).diff(moment(startedAt), 'seconds', true));

    if (totalSeconds < 60) {
      return `${totalSeconds}s`;
    }

    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}m ${seconds}s`;
  }

  selectVerification(index: number): void {
    if (index === this.currentIndex) return;
    this.currentIndex = index;
    this.loadDetail(this.verifications[index].id);
  }

  ngOnDestroy(): void {
    this._unsubscribe.next();
    this._unsubscribe.complete();
    if (this.currentDetail) {
      this.idVerificationSocket.leaveVerificationRoom(this.currentDetail.id);
    }
  }
}